import { useTranslations } from 'next-intl';
import { HeaderIconButton } from '@mentimeter/editor-header';
import { tracking } from '@mentimeter/http-clients';
import { CopyIcon } from '@mentimeter/ragnar-visuals';
import { getRegionBySeriesId } from '@mentimeter/region';
import userCache from '@mentimeter/user';
import { useCallback } from 'react';
import { postDuplicateRequest } from '@core-api/view-only/view-only/{slide_deck_public_key}/duplicate';
import { trackUser } from '@api/tracking/client';
import { captureException } from '@mentimeter/errors/sentry';
import { MentiError } from '@mentimeter/error-utils/error';
import { Tooltip } from '@mentimeter/ragnar-ui/tooltip';
import {
  useButtonState,
  type RagnarButtonState,
} from '@mentimeter/ragnar-ui/button';
import type { IconButtonProps } from '@mentimeter/ragnar-ui/icon-button';
import AuthModalClicker from 'components/AuthModalClicker';
import { useReferral } from '@mentimeter/auth';
import { useSearchParams } from '@mentimeter/next-navigation';
import { buildPresentationPath } from '@mentimeter/presentation-url-utils';
import { getLocalStorageVisitorToken } from '@mentimeter/visitor/visitor-token';

const CopyButtonWithTooltip = ({
  buttonState,
  buttonSize,
  onClick,
}: {
  buttonState: RagnarButtonState | undefined;
  buttonSize: IconButtonProps['size'];
  onClick: () => void;
}) => {
  const t = useTranslations('creation.header');
  return (
    <Tooltip
      side="bottom"
      trigger={
        <HeaderIconButton
          id="copy-to-my-presentations-button"
          aria-label={t('header_copy_to_account_a11y')}
          size={buttonSize}
          onClick={onClick}
          variant="secondary"
          state={buttonState}
          disabled={buttonState !== undefined}
        >
          <CopyIcon />
        </HeaderIconButton>
      }
    >
      {buttonState === 'success'
        ? t('header_copy_success_message')
        : t('header_copy_to_my_presentations_message')}
    </Tooltip>
  );
};

export const CopyToAccountVisitor = ({
  seriesId,
  buttonSize,
  trackingContext,
}: {
  seriesId: string;
  buttonSize: IconButtonProps['size'];
  trackingContext: string;
}) => {
  const referral = useReferral();
  const searchParams = useSearchParams();
  const continueUrl = buildPresentationPath({
    seriesId,
    mode: 'view',
    currentSearchParams: searchParams,
  });

  return (
    <AuthModalClicker
      showSignup
      continueUrl={continueUrl}
      referral={referral}
      render={({ onClick: onAuthClick }) => (
        <CopyButtonWithTooltip
          buttonState={undefined}
          buttonSize={buttonSize}
          onClick={() => {
            onAuthClick();

            const visitorToken = getLocalStorageVisitorToken();
            if (!visitorToken) return;

            tracking().trackVisitor(
              {
                event: 'Duplicated presentations',
                properties: { context: trackingContext },
              },
              visitorToken,
            );
          }}
        />
      )}
    />
  );
};

export const CopyToAccountUser = ({
  seriesId,
  buttonSize,
  trackingContext,
}: {
  seriesId: string;
  buttonSize: IconButtonProps['size'];
  trackingContext: string;
}) => {
  const [buttonState, setButtonState] = useButtonState();

  const copyPresentationToAccount = useCallback(async () => {
    setButtonState('loading');
    try {
      await fetch(
        postDuplicateRequest({
          slideDeckPublicKey: seriesId,
          region: userCache.region,
          resourceRegion: getRegionBySeriesId(seriesId),
          userAuth: userCache.getToken(),
          duplicationStrategy: 'slide-deck',
        }),
      );

      setButtonState('success');
      setTimeout(() => setButtonState(undefined), 6000);
      trackUser({
        event: 'Duplicated presentations',
        properties: { context: trackingContext },
      });
    } catch (err) {
      setButtonState('error');
      setTimeout(() => setButtonState(undefined), 3000);
      captureException(
        new MentiError('Failed to copy presentation from view only header', {
          feature: 'copy-to-account',
          cause: err,
        }),
      );
    }
  }, [setButtonState, seriesId, trackingContext]);

  return (
    <CopyButtonWithTooltip
      buttonState={buttonState}
      buttonSize={buttonSize}
      onClick={copyPresentationToAccount}
    />
  );
};
